import React, { useState, useEffect } from 'react';
import api from '../services/api';

// Suppression du CLIENT_ID en dur

const WIDGETS = [
  { id: 'metrics',     name: 'Métriques clés',      desc: 'CA, TVA, achats du mois',         icon: '▦', color: 'green',  locked: true,  size: 'full',   sizes: ['full'] },
  { id: 'tva',         name: 'Déclaration TVA',      desc: 'Collectée, déductible, net',      icon: '%', color: 'amber',  locked: true,  size: 'half',   sizes: ['half'] },
  { id: 'docs',        name: 'Documents récents',    desc: 'Statut OCR, validation',          icon: '≡', color: 'blue',   locked: false, size: 'half',   sizes: ['half', 'full'] },
  { id: 'chat',        name: 'Assistant IA',         desc: 'Questions en langage naturel',    icon: '◎', color: 'green',  locked: false, size: 'half',   sizes: ['half', 'full'] },
  { id: 'charges',     name: 'Répartition charges',  desc: 'Par compte PCG, graphique',       icon: '◈', color: 'purple', locked: false, size: 'half',   sizes: ['half', 'full'] },
  { id: 'echeances',   name: 'Échéances fiscales',   desc: 'CA3, IS, cotisations',            icon: '◷', color: 'coral',  locked: false, size: 'half',   sizes: ['half', 'full'] },
  { id: 'tresorerie',  name: 'Trésorerie',           desc: 'Solde estimé, flux entrants',     icon: '↕', color: 'blue',   locked: false, size: 'half',   sizes: ['half', 'full'] },
  { id: 'fournisseurs',name: 'Top fournisseurs',     desc: 'Classement par montant',          icon: '↑', color: 'amber',  locked: false, size: 'half',   sizes: ['half', 'full'], isNew: true },
  { id: 'audit',       name: 'Piste d\'audit',       desc: 'Dernières modifications',         icon: '⊙', color: 'coral',  locked: false, size: 'half',   sizes: ['half', 'full'], isNew: true },
];

// Hook pour la détection mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

const DashboardConfigurator = ({ clientId }) => {
  const isMobile = useIsMobile();
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get(`/dashboard/${clientId}/config`);
      if (response.success && response.data.widgets) {
        // Convertir le tableau de widgets en objet de config
        const configMap = {};
        response.data.widgets.forEach(w => {
          configMap[w.id] = { active: w.active, size: w.size };
        });
        setConfig(configMap);
      } else {
        // Config par défaut
        initDefaultConfig();
      }
    } catch (err) {
      console.error('Erreur chargement config:', err);
      initDefaultConfig();
    } finally {
      setLoading(false);
    }
  };

  const initDefaultConfig = () => {
    const defaultConfig = {};
    WIDGETS.forEach(w => {
      defaultConfig[w.id] = {
        active: w.locked || ['metrics', 'tva', 'docs', 'chat'].includes(w.id),
        size: w.size
      };
    });
    setConfig(defaultConfig);
  };

  const toggleWidget = (id) => {
    const widget = WIDGETS.find(w => w.id === id);
    if (widget.locked) return;
    setConfig(prev => ({
      ...prev,
      [id]: { ...prev[id], active: !prev[id].active }
    }));
  };

  const setWidgetSize = (id, size) => {
    const widget = WIDGETS.find(w => w.id === id);
    if (widget.locked) return;
    setConfig(prev => ({
      ...prev,
      [id]: { ...prev[id], size }
    }));
  };

  const resetConfig = () => {
    initDefaultConfig();
    setSaveStatus(null);
  };

  const saveConfig = async () => {
    try {
      // Convertir la config en tableau de widgets
      const widgets = WIDGETS.map(w => ({
        id: w.id,
        active: config[w.id]?.active ?? (w.locked || ['metrics', 'tva', 'docs', 'chat'].includes(w.id)),
        size: config[w.id]?.size ?? w.size,
        position: WIDGETS.findIndex(x => x.id === w.id)
      }));

      const response = await api.post(`/dashboard/${clientId}/config`, { widgets });
      if (response.success) {
        setSaveStatus({ type: 'success', message: 'Configuration sauvegardée avec succès' });
        setTimeout(() => setSaveStatus(null), 3000);
      }
    } catch (err) {
      console.error('Erreur sauvegarde config:', err);
      setSaveStatus({ type: 'error', message: 'Erreur lors de la sauvegarde' });
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" style={{background: '#F5F4ED'}}>
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-green-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cfg" style={{fontFamily: 'Inter, system-ui, sans-serif', padding: isMobile ? '12px' : '24px', background: '#F5F4ED', minHeight: '100vh'}}>
      {/* Header */}
      <div className="cfg-header" style={{display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', padding: isMobile ? '10px 0' : '14px 0 12px', borderBottom: '1px solid #E8E7E3', marginBottom: '16px', gap: isMobile ? '12px' : '0'}}>
        <div>
          <div className="cfg-title" style={{fontSize: '15px', fontWeight: '500', color: '#2D2D2D'}}>Personnaliser mon tableau de bord</div>
          <div className="cfg-sub" style={{fontSize: '12px', color: '#73726C', marginTop: '2px'}}>Activez les blocs utiles pour votre activité</div>
        </div>
        <div className="header-btns" style={{display: 'flex', gap: '8px', width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-end'}}>
          <button
            className="btn"
            style={{fontSize: '12px', padding: '6px 12px', border: '1px solid #E8E7E3', borderRadius: '8px', background: 'transparent', color: '#2D2D2D', cursor: 'pointer', flex: isMobile ? 1 : 'initial', textAlign: 'center'}}
            onClick={resetConfig}
          >
            Réinitialiser
          </button>
          <button
            className="btn primary"
            style={{fontSize: '12px', padding: '6px 12px', border: '1px solid #1D9E75', borderRadius: '8px', background: '#1D9E75', color: 'white', cursor: 'pointer', flex: isMobile ? 1 : 'initial', textAlign: 'center'}}
            onClick={saveConfig}
          >
            Enregistrer
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="cfg-body" style={{display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px'}}>
        {/* Widget List */}
        <div>
          <div className="section-title" style={{fontSize: '11px', fontWeight: '500', color: '#73726C', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px'}}>
            Blocs disponibles
          </div>
          <div className="widget-list" style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
            {WIDGETS.map(widget => {
              const isActive = config[widget.id]?.active ?? (widget.locked || ['metrics', 'tva', 'docs', 'chat'].includes(widget.id));
              return (
                <div
                  key={widget.id}
                  className={`widget-card ${isActive ? 'active' : ''} ${widget.locked ? 'locked' : ''}`}
                  style={{
                    background: 'white',
                    border: '1px solid #E8E7E3',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: widget.locked ? 'not-allowed' : 'pointer',
                    transition: 'border-color 0.15s, background 0.15s',
                    opacity: widget.locked ? 0.7 : 1
                  }}
                  onClick={() => !widget.locked && toggleWidget(widget.id)}
                  onMouseEnter={(e) => { if (!widget.locked) e.currentTarget.style.background = '#FAF9F5'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
                >
                  <div
                    className={`widget-icon ${widget.color}`}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      flexShrink: 0,
                      background: widget.color === 'green' ? '#E1F5EE' :
                                 widget.color === 'blue' ? '#E6F1FB' :
                                 widget.color === 'amber' ? '#FAEEDA' :
                                 widget.color === 'coral' ? '#FAECE7' :
                                 widget.color === 'purple' ? '#EEEDFE' : '#FAF9F5'
                    }}
                  >
                    {widget.icon}
                  </div>
                  <div className="widget-info" style={{flex: 1, minWidth: 0}}>
                    <div className="widget-name" style={{fontSize: '12px', fontWeight: '500', color: '#2D2D2D'}}>
                      {widget.name}
                      {widget.isNew && <span style={{fontSize: '9px', fontWeight: '500', padding: '1px 5px', background: '#E1F5EE', color: '#0F6E56', borderRadius: '4px', marginLeft: '4px'}}>nouveau</span>}
                    </div>
                    <div className="widget-desc" style={{fontSize: '11px', color: '#73726C', marginTop: '1px'}}>{widget.desc}</div>
                  </div>
                  {widget.locked ? (
                    <div style={{fontSize: '10px', padding: '2px 6px', background: '#FAF9F5', color: '#73726C', borderRadius: '4px', flexShrink: 0}}>
                      obligatoire
                    </div>
                  ) : (
                    <div
                      className={`widget-toggle ${isActive ? 'on' : ''}`}
                      style={{
                        width: '32px',
                        height: '18px',
                        borderRadius: '9px',
                        background: isActive ? '#1D9E75' : '#E8E7E3',
                        position: 'relative',
                        flexShrink: 0,
                        transition: 'background 0.2s'
                      }}
                    >
                      <div
                        style={{
                          content: '""',
                          position: 'absolute',
                          width: '14px',
                          height: '14px',
                          borderRadius: '50%',
                          background: 'white',
                          top: '2px',
                          left: isActive ? '16px' : '2px',
                          transition: 'left 0.2s'
                        }}
                      ></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Preview Panel */}
        <div>
          <div className="section-title" style={{fontSize: '11px', fontWeight: '500', color: '#73726C', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px'}}>
            Aperçu de votre dashboard
          </div>
          <div className="preview-panel" style={{background: '#FAF9F5', borderRadius: '12px', padding: '14px'}}>
            <div className="preview-grid" style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
              {WIDGETS.map(widget => {
                const isActive = config[widget.id]?.active ?? (widget.locked || ['metrics', 'tva', 'docs', 'chat'].includes(widget.id));
                const currentSize = config[widget.id]?.size ?? widget.size;
                const canChangeSize = !widget.locked && widget.sizes.length > 1;

                return (
                  <div
                    key={widget.id}
                    className={`preview-slot ${isActive ? '' : 'hidden'}`}
                    style={{
                      background: 'white',
                      border: '1px solid #E8E7E3',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'opacity 0.2s',
                      opacity: isActive ? 1 : 0.3,
                      borderStyle: isActive ? 'solid' : 'dashed'
                    }}
                  >
                    <div className="preview-slot-icon" style={{fontSize: '13px'}}>{widget.icon}</div>
                    <div className="preview-slot-info" style={{flex: 1}}>
                      <div className="preview-slot-name" style={{fontSize: '12px', fontWeight: '500', color: '#2D2D2D'}}>{widget.name}</div>
                      {canChangeSize && (
                        <div className="size-selector" style={{display: 'flex', gap: '4px', marginTop: '4px'}}>
                          {widget.sizes.map(size => (
                            <button
                              key={size}
                              className={`size-btn ${currentSize === size ? 'sel' : ''}`}
                              style={{
                                fontSize: '10px',
                                padding: '2px 6px',
                                border: '1px solid #E8E7E3',
                                borderRadius: '4px',
                                background: currentSize === size ? '#1D9E75' : 'transparent',
                                color: currentSize === size ? 'white' : '#73726C',
                                cursor: 'pointer'
                              }}
                              onClick={(e) => { e.stopPropagation(); setWidgetSize(widget.id, size); }}
                            >
                              {size === 'full' ? 'Pleine largeur' : 'Demi'}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {widget.locked ? (
                      <div style={{fontSize: '10px', color: '#73726C'}}>verrouillé</div>
                    ) : isActive ? (
                      <div style={{color: '#73726C', fontSize: '14px', cursor: 'grab'}}>⠿</div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="config-footer" style={{marginTop: '16px', padding: '12px 14px', background: '#FAF9F5', borderRadius: '8px', fontSize: '11px', color: '#73726C', display: 'flex', alignItems: 'center', gap: '8px'}}>
        <div className="info-dot" style={{width: '6px', height: '6px', borderRadius: '50%', background: '#1D9E75', flexShrink: 0}}></div>
        <span>
          {saveStatus ? saveStatus.message : 'Les blocs verrouillés sont obligatoires pour votre conformité fiscale et ne peuvent pas être désactivés.'}
        </span>
      </div>
    </div>
  );
};

export default DashboardConfigurator;
